<template>
  <section class="container-fluid mt-5 py-5">
    <div class="container mt-5">
      <!-- Header Section -->
      <div class="row align-items-center mb-4">
        <div class="col-md-6">
          <h2 class="text-light">
            Bancos de Semillas<span class="text-primary">.</span>
          </h2>
        </div>
        <div class="col-md-6 text-md-end">
          <router-link to="/admin/bancos/nuevo" class="btn btn-primary">
            <i class="fas fa-plus me-2"></i>Agregar Banco
          </router-link>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Banks Grid -->
      <div v-else-if="banks.length" class="row g-4">
        <div v-for="bank in banks" :key="bank.brand_id" class="col-md-6 col-lg-4">
          <div class="card bg-dark text-light h-100">
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <img 
                  :src="bank.image" 
                  :alt="bank.brand_name"
                  class="img-thumbnail me-3"
                  style="width: 80px; height: 80px; object-fit: contain;"
                />
                <div class="flex-grow-1">
                  <h3 class="card-title mb-1 fs-5 fw-bold">{{ bank.brand_name }}</h3>
                  <span class="badge" :class="bank.is_national ? 'bg-success' : 'bg-info'">
                    {{ bank.is_national ? 'Nacional' : 'Internacional' }}
                  </span>
                </div>
              </div>
              
              <p class="card-text text-muted mb-3">
                {{ truncateText(bank.description || 'Sin descripción', 100) }}
              </p>

              <!-- Información de descuento si existe -->
              <div v-if="bank.discount_active" class="alert alert-success py-2 mb-3">
                <div class="d-flex align-items-center">
                  <i class="fas fa-percentage me-2"></i>
                  <small class="fw-bold">
                    Descuento activo: 
                    {{ bank.discount_type === 'percentage' ? bank.discount_value + '%' : '$' + bank.discount_value }}
                    {{ bank.discount_type === 'percentage' ? 'de descuento' : 'de descuento fijo' }}
                  </small>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <small class="text-muted">
                  Creado: {{ formatDate(bank.created_at) }}
                </small>
                <button 
                  @click="toggleNational(bank)"
                  class="btn btn-sm btn-outline-warning"
                  :disabled="isUpdating"
                >
                  <i class="fas fa-flag me-1"></i>
                  {{ bank.is_national ? 'Hacer Internacional' : 'Hacer Nacional' }}
                </button>
              </div>
              
              <div class="d-flex justify-content-end gap-2">
                <button 
                  @click="openDiscountModal(bank)"
                  class="btn btn-sm btn-outline-success"
                  :class="{ 'btn-success': bank.discount_active }"
                >
                  <i class="fas fa-percentage me-1"></i>
                  {{ bank.discount_active ? 'Editar Descuento' : 'Aplicar Descuento' }}
                </button>
                <router-link 
                  :to="`/admin/bancos/editar/${bank.id}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  <i class="fas fa-edit me-1"></i>Editar
                </router-link>
                <button 
                  @click="confirmDelete(bank)"
                  class="btn btn-sm btn-outline-danger"
                >
                  <i class="fas fa-trash-alt me-1"></i>Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <i class="fas fa-seedling fa-3x mb-3 text-muted"></i>
        <h4 class="text-muted">No hay bancos registrados</h4>
        <router-link to="/admin/bancos/nuevo" class="btn btn-primary mt-3">
          Agregar el primer banco
        </router-link>
      </div>
    </div>
  </section>

  <!-- Modal de descuento -->
  <BrandDiscountModal 
    :show="showDiscountModal" 
    :brand="selectedBank" 
    @close="closeDiscountModal" 
    @success="handleDiscountSuccess" 
  />
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { getBrands, deleteBrand, updateBrand } from '@/services/brands';
import BrandDiscountModal from '@/components/admin/BrandDiscountModal.vue';

const toast = useToast();
const banks = ref([]);
const isLoading = ref(true);
const error = ref(null);
const isUpdating = ref(false);
const showDiscountModal = ref(false);
const selectedBank = ref(null);

const fetchBanks = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    const data = await getBrands();
    banks.value = data;
  } catch (err) {
    error.value = 'Error al cargar los bancos';
    toast.error(error.value);
  } finally {
    isLoading.value = false;
  }
};

const toggleNational = async (bank) => {
  try {
    isUpdating.value = true;
    await updateBrand(bank.id, {
      ...bank,
      is_national: !bank.is_national
    });
    
    // Actualizar la marca en la lista local
    const index = banks.value.findIndex(b => b.id === bank.id);
    if (index !== -1) {
      banks.value[index] = { ...banks.value[index], is_national: !bank.is_national };
    }
    
    toast.success(`Banco ${bank.brand_name} cambiado a ${!bank.is_national ? 'Nacional' : 'Internacional'}`);
  } catch (error) {
    console.error('Error al cambiar el tipo de banco:', error);
    toast.error('Error al cambiar el tipo de banco');
  } finally {
    isUpdating.value = false;
  }
};

const truncateText = (text, length) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const confirmDelete = async (bank) => {
  if (window.confirm(`¿Estás seguro de eliminar el banco ${bank.brand_name}?`)) {
    try {
      await deleteBrand(bank.id);
      toast.success('Banco eliminado exitosamente');
      await fetchBanks(); // Recargar la lista
    } catch (err) {
      toast.error('Error al eliminar el banco');
    }
  }
};

const openDiscountModal = (bank) => {
  selectedBank.value = bank;
  showDiscountModal.value = true;
};

const closeDiscountModal = () => {
  showDiscountModal.value = false;
  selectedBank.value = null;
};

const handleDiscountSuccess = (updatedBank) => {
  // Actualizar el banco en la lista local
  const index = banks.value.findIndex(b => b.id === updatedBank.id);
  if (index !== -1) {
    banks.value[index] = { ...banks.value[index], ...updatedBank };
  }
};

onMounted(fetchBanks);
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-5px);
}

.img-thumbnail {
  background-color: var(--bs-dark);
  border-color: var(--bs-gray-700);
}

.btn-group .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-outline-primary:hover {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
}

.btn-outline-danger:hover {
  background-color: var(--bs-danger);
  border-color: var(--bs-danger);
  color: white;
}

.btn-outline-warning:hover {
  background-color: var(--bs-warning);
  border-color: var(--bs-warning);
  color: var(--bs-dark);
}

.badge {
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline-success.btn-success {
  background-color: var(--bs-success);
  border-color: var(--bs-success);
  color: white;
}

.btn-outline-success.btn-success:hover {
  background-color: var(--bs-success);
  border-color: var(--bs-success);
  color: white;
  opacity: 0.9;
}

.alert-success {
  background-color: rgba(25, 135, 84, 0.1);
  border: 1px solid rgba(25, 135, 84, 0.2);
  border-radius: 0.375rem;
}

.alert-success small {
  color: var(--bs-success);
}
</style>
